import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, X, ChevronRight } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { apiFetch } from '@/lib/api';
import { useAuthContext } from '@/context/AuthContext';
import { usePaginatedList } from '@/hooks/usePaginatedList';
import { formatCents } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableTh } from '@/components/SortableTh';
import type { Supplier } from '@billflow/shared';

// ─── Supplier Modal ────────────────────────────────────────────────────────────

interface SupplierModalProps {
  mode: 'create' | 'edit';
  supplier?: Supplier;
  onClose: () => void;
  onSaved: () => void;
}

function SupplierModal({ mode, supplier, onClose, onSaved }: SupplierModalProps) {
  const [code, setCode] = useState(supplier?.code ?? '');
  const [name, setName] = useState(supplier?.name ?? '');
  const [contactPerson, setContactPerson] = useState(supplier?.contact_person ?? '');
  const [phone, setPhone] = useState(supplier?.phone ?? '');
  const [email, setEmail] = useState(supplier?.email ?? '');
  const [address, setAddress] = useState(supplier?.address ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const body = {
        code: code.trim() || undefined,
        name: name.trim(),
        // Send '' (not undefined) so clearing an optional field persists — JSON.stringify
        // drops undefined keys, which on PATCH means "leave unchanged" (can't remove a value).
        contact_person: contactPerson.trim(),
        phone: phone.trim(),
        email: email.trim(),
        address: address.trim(),
      };

      if (mode === 'create') {
        await apiFetch('/api/suppliers', { method: 'POST', body: JSON.stringify(body) });
        toast.success('Supplier created');
      } else {
        await apiFetch(`/api/suppliers/${supplier!.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
        toast.success('Supplier updated');
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">
            {mode === 'create' ? 'New Supplier' : 'Edit Supplier'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Row: Code + Name */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="s-code">Code</Label>
              <Input
                id="s-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Auto-generated if blank"
                disabled={loading}
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="s-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Contact Person */}
          <div className="space-y-1.5">
            <Label htmlFor="s-contact">Contact Person</Label>
            <Input
              id="s-contact"
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Row: Phone + Email */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="s-phone">Phone</Label>
              <Input
                id="s-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-email">Email</Label>
              <Input
                id="s-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          {/* Address */}
          <div className="space-y-1.5">
            <Label htmlFor="s-address">Address</Label>
            <textarea
              id="s-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              disabled={loading}
              rows={2}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : mode === 'create' ? 'Create Supplier' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Delete Confirm Dialog ─────────────────────────────────────────────────────

interface DeleteDialogProps {
  supplier: Supplier;
  onCancel: () => void;
  onConfirm: () => void;
  loading: boolean;
}

function DeleteDialog({ supplier, onCancel, onConfirm, loading }: DeleteDialogProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Delete Supplier</h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Delete <span className="font-semibold text-foreground">{supplier.name}</span>? This
            cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={onConfirm} disabled={loading}>
              {loading ? 'Deleting…' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

type StatusFilter = 'active' | 'inactive' | 'all';

const statusTabs: { label: string; value: StatusFilter }[] = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
  { label: 'All', value: 'all' },
];

export default function SuppliersPage() {
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<StatusFilter>('active');
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | undefined>(undefined);
  const [deletingSupplier, setDeletingSupplier] = useState<Supplier | undefined>(undefined);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const {
    rows: suppliers,
    total,
    page,
    setPage,
    limit,
    setLimit,
    sort,
    dir,
    toggleSort,
    q,
    setQ,
    isLoading,
  } = usePaginatedList<Supplier>({
    endpoint: '/api/suppliers',
    entityKey: 'suppliers',
    defaultSort: 'created',
    defaultDir: 'desc',
    extraParams: { status: status !== 'all' ? status : undefined },
  });

  function refetch() {
    queryClient.invalidateQueries({ queryKey: ['suppliers'] });
  }

  function openCreate() {
    setEditingSupplier(undefined);
    setModalMode('create');
  }

  function openEdit(s: Supplier) {
    setEditingSupplier(s);
    setModalMode('edit');
  }

  async function handleDelete() {
    if (!deletingSupplier) return;
    setDeleteLoading(true);
    try {
      await apiFetch(`/api/suppliers/${deletingSupplier.id}`, { method: 'DELETE' });
      toast.success('Supplier deleted');
      setDeletingSupplier(undefined);
      refetch();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete supplier';
      toast.error(msg);
      setDeletingSupplier(undefined);
    } finally {
      setDeleteLoading(false);
    }
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">Suppliers</h1>
          {!isLoading && (
            <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
              {total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons
            endpoint="/api/suppliers"
            params={{
              q: q || undefined,
              status: status !== 'all' ? status : undefined,
            }}
          />
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1.5" />
            New Supplier
          </Button>
        </div>
      </div>

      {/* Filters — one line: search · status pills (wraps on small screens) */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, code, phone…"
            className="pl-9 h-9"
          />
        </div>

        {/* Status pills */}
        <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5 shrink-0">
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              type="button"
              onClick={() => setStatus(tab.value)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                status === tab.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <SortableTh
                label="Code"
                sortKey="code"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-28"
              />
              <SortableTh label="Name" sortKey="name" sort={sort} dir={dir} onToggle={toggleSort} />
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden sm:table-cell">
                Phone
              </th>
              <th className="px-4 py-3 text-left font-medium text-muted-foreground hidden md:table-cell">
                Email
              </th>
              <th className="px-4 py-3 text-right font-medium text-muted-foreground w-32">
                Outstanding
              </th>
              <SortableTh
                label="Status"
                sortKey="status"
                sort={sort}
                dir={dir}
                onToggle={toggleSort}
                className="w-24"
              />
              <th className="px-4 py-3 text-right font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && suppliers.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                  No suppliers found.
                </td>
              </tr>
            )}
            {suppliers.map((s) => (
              <tr key={`supplier-${s.id}`} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 font-mono text-xs">
                  <Link
                    to={`/suppliers/${s.id}`}
                    className="text-muted-foreground hover:text-primary hover:underline"
                  >
                    {s.code ?? '—'}
                  </Link>
                </td>
                <td className="px-4 py-3 font-medium">
                  <Link to={`/suppliers/${s.id}`} className="text-primary hover:underline">
                    {s.name}
                  </Link>
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden sm:table-cell">
                  {s.phone ?? '—'}
                </td>
                <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">
                  {s.email ?? '—'}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {(s.outstanding_balance ?? 0) > 0 ? (
                    <span className="text-amber-700 font-medium">
                      {formatCents(s.outstanding_balance ?? 0)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{formatCents(0)}</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {s.status === 'active' ? (
                    <Badge variant="green">Active</Badge>
                  ) : (
                    <Badge variant="red">Inactive</Badge>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <Link to={`/suppliers/${s.id}`}>
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs">
                        View
                        <ChevronRight className="h-3 w-3 ml-0.5" />
                      </Button>
                    </Link>
                    {isAdmin && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => openEdit(s)}
                        >
                          <Pencil className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:border-destructive/50"
                          onClick={() => setDeletingSupplier(s)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && total > 0 && (
        <Pagination
          page={page}
          limit={limit}
          total={total}
          onPageChange={setPage}
          onLimitChange={setLimit}
        />
      )}

      {/* Modals */}
      {modalMode && (
        <SupplierModal
          mode={modalMode}
          supplier={editingSupplier}
          onClose={() => {
            setModalMode(null);
            setEditingSupplier(undefined);
          }}
          onSaved={refetch}
        />
      )}

      {deletingSupplier && (
        <DeleteDialog
          supplier={deletingSupplier}
          onCancel={() => setDeletingSupplier(undefined)}
          onConfirm={handleDelete}
          loading={deleteLoading}
        />
      )}
    </div>
  );
}
